const BASE_PALETTE = {
    clockBody: '#40494d',
    shadowInner: 'rgba(0, 0, 0, 0.6)',
    highlightInner: 'rgba(255, 255, 255, 0.08)',
    rimLight: '#59656b',
    rimDark: '#1b2023',
    handHour: '#f4f6f7',
    handMinute: '#cfd4d6',
    pinBase: '#2a3033'
};

const SHADOW_BASE = 'rgba(10, 14, 16, 0.5)';

const SHUL_STATION_MARK_VIEWBOX_WIDTH = 36.629;
const SHUL_STATION_MARK_VIEWBOX_HEIGHT = 36;
const SHUL_STATION_MARK_PATH = new Path2D(
    'M0 0V36H36.629V0H0ZM20.3969 33.3999H16.2359V20.4887C16.2359 19.3595 17.1694 18.4457 18.3145 18.4457C19.4597 18.4457 20.3931 19.3632 20.3931 20.4887V33.3999H20.3969ZM27.1997 33.3999H23.0424V20.4887C23.0424 17.9257 20.9222 15.8456 18.3183 15.8456C15.7144 15.8456 13.5942 17.9294 13.5942 20.4887V33.3999H9.43693V20.4961C9.43693 15.6822 13.4241 11.7635 18.3221 11.7635C23.2201 11.7635 27.2072 15.6822 27.2072 20.4961V33.3999H27.1997ZM33.9835 33.3999H29.8452V20.4961C29.8452 14.2485 24.6713 9.16343 18.3145 9.16343C11.9577 9.16343 6.78386 14.2485 6.78386 20.4961V33.3999H2.64551V2.60008H33.9835V33.3999Z'
);

function tint(baseColor, accentColor, accentAmount) {
    return `color-mix(in srgb, ${baseColor} ${100 - accentAmount}%, ${accentColor} ${accentAmount}%)`;
}

function buildThemedPalette(accentColor) {
    return {
        clockBody: tint(BASE_PALETTE.clockBody, accentColor, 12),
        shadowBase: SHADOW_BASE,
        shadowInner: BASE_PALETTE.shadowInner,
        highlightInner: BASE_PALETTE.highlightInner,
        rimLight: tint(BASE_PALETTE.rimLight, accentColor, 20),
        rimDark: tint(BASE_PALETTE.rimDark, accentColor, 14),
        handHour: BASE_PALETTE.handHour,
        handMinute: BASE_PALETTE.handMinute,
        handSecond: tint(accentColor, '#ffffff', 25),
        handShadow: SHADOW_BASE,
        pinBase: BASE_PALETTE.pinBase
    };
}

export class AnalogClock {
    constructor(canvas) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d');
        this.staticCanvas = document.createElement('canvas');
        this.staticCtx = this.staticCanvas.getContext('2d');
        this.animationFrameId = null;
        this.resizeObserver = new ResizeObserver(() => this.resize());
        this.resizeObserver.observe(canvas);
        this.resize();
        this.start();
    }

    readPalette() {
        const accentColor = getComputedStyle(this.canvas).getPropertyValue('--accent').trim() || '#6E5DFF';
        this.palette = buildThemedPalette(accentColor);
    }

    resize() {
        const dpr = window.devicePixelRatio || 1;
        const canvasRect = this.canvas.getBoundingClientRect();
        const size = Math.max(0, Math.min(canvasRect.width, canvasRect.height));
        const footprintRect = this.canvas.parentElement.getBoundingClientRect();
        const radius = Math.max(0, Math.min(footprintRect.width, footprintRect.height) / 2);

        if (this.size === size && this.radius === radius) {
            return;
        }

        this.size = size;
        this.radius = radius;
        this.canvas.width = size * dpr;
        this.canvas.height = size * dpr;
        this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        this.staticCanvas.width = size * dpr;
        this.staticCanvas.height = size * dpr;
        this.staticCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
        this.center = size / 2;
        this.readPalette();
        this.renderStaticLayer();
        this.draw();
    }

    renderStaticLayer() {
        if (this.size <= 0 || this.radius <= 4 + this.radius * 0.05) {
            return;
        }

        const activeCtx = this.ctx;
        this.ctx = this.staticCtx;
        this.staticCtx.clearRect(0, 0, this.size, this.size);
        this.drawFace();
        this.faceRadius = this.drawRim();
        this.drawShulStationMark(this.faceRadius);
        this.ctx = activeCtx;
    }

    drawFace() {
        const ctx = this.ctx;
        ctx.save();
        ctx.beginPath();
        ctx.arc(this.center, this.center, this.radius - 4, 0, Math.PI * 2);
        ctx.shadowColor = this.palette.shadowBase;
        ctx.shadowBlur = this.radius * 0.18;
        ctx.shadowOffsetX = this.radius * 0.08;
        ctx.shadowOffsetY = this.radius * 0.1;
        ctx.fillStyle = this.palette.clockBody;
        ctx.fill();
        ctx.restore();
    }

    drawRim() {
        const ctx = this.ctx;
        const outerRadius = this.radius - 4;
        ctx.beginPath();
        ctx.arc(this.center, this.center, outerRadius, 0, Math.PI * 2);
        const rimGradient = ctx.createLinearGradient(
            this.center - this.radius, this.center - this.radius,
            this.center + this.radius, this.center + this.radius
        );
        rimGradient.addColorStop(0, this.palette.rimLight);
        rimGradient.addColorStop(1, this.palette.rimDark);
        ctx.fillStyle = rimGradient;
        ctx.fill();

        const faceRadius = outerRadius - this.radius * 0.05;
        ctx.beginPath();
        ctx.arc(this.center, this.center, faceRadius, 0, Math.PI * 2);
        ctx.fillStyle = this.palette.clockBody;
        ctx.fill();

        ctx.save();
        ctx.beginPath();
        ctx.arc(this.center, this.center, faceRadius, 0, Math.PI * 2);
        ctx.clip();
        ctx.beginPath();
        ctx.arc(this.center, this.center, faceRadius + 5, 0, Math.PI * 2);
        ctx.lineWidth = this.radius * 0.08;
        const innerShadowGradient = ctx.createLinearGradient(
            this.center - this.radius, this.center - this.radius,
            this.center + this.radius, this.center + this.radius
        );
        innerShadowGradient.addColorStop(0, this.palette.shadowInner);
        innerShadowGradient.addColorStop(1, this.palette.highlightInner);
        ctx.strokeStyle = innerShadowGradient;
        ctx.stroke();
        ctx.restore();

        return faceRadius;
    }

    drawHand(angle, length, tailLength, hubWidth, tipWidth, color) {
        const ctx = this.ctx;
        const hubHalf = hubWidth / 2;
        const tipHalf = tipWidth / 2;
        const tipX = length - tipHalf;
        const backX = -tailLength;

        ctx.save();
        ctx.translate(this.center, this.center);
        ctx.rotate(angle);
        ctx.shadowColor = this.palette.handShadow;
        ctx.shadowBlur = this.radius * 0.025;
        ctx.shadowOffsetX = this.radius * 0.012;
        ctx.shadowOffsetY = this.radius * 0.018;

        ctx.beginPath();
        ctx.moveTo(backX, -hubHalf);
        ctx.lineTo(tipX, -tipHalf);
        ctx.arc(tipX, 0, tipHalf, -Math.PI / 2, Math.PI / 2);
        ctx.lineTo(backX, hubHalf);
        ctx.closePath();

        const bodyGradient = ctx.createLinearGradient(0, -hubHalf, 0, hubHalf);
        bodyGradient.addColorStop(0, tint(color, '#ffffff', 35));
        bodyGradient.addColorStop(0.45, color);
        bodyGradient.addColorStop(1, tint(color, '#000000', 30));
        ctx.fillStyle = bodyGradient;
        ctx.fill();

        ctx.beginPath();
        ctx.moveTo(backX, -hubHalf * 0.2);
        ctx.lineTo(tipX, -tipHalf * 0.2);
        ctx.strokeStyle = tint(color, '#ffffff', 55);
        ctx.globalAlpha = 0.5;
        ctx.lineWidth = Math.max(0.6, tipWidth * 0.18);
        ctx.stroke();
        ctx.globalAlpha = 1;

        ctx.restore();
    }

    drawCenterPin(faceRadius) {
        const ctx = this.ctx;
        const pinRadius = faceRadius * 0.055;

        ctx.save();
        ctx.beginPath();
        ctx.arc(this.center, this.center, pinRadius, 0, Math.PI * 2);
        ctx.shadowColor = this.palette.shadowBase;
        ctx.shadowBlur = pinRadius * 0.4;
        ctx.shadowOffsetX = pinRadius * 0.1;
        ctx.shadowOffsetY = pinRadius * 0.15;
        const pinGradient = ctx.createLinearGradient(
            this.center - pinRadius, this.center - pinRadius,
            this.center + pinRadius, this.center + pinRadius
        );
        pinGradient.addColorStop(0, this.palette.rimLight);
        pinGradient.addColorStop(1, this.palette.rimDark);
        ctx.fillStyle = pinGradient;
        ctx.fill();
        ctx.restore();
    }

    drawShulStationMark(faceRadius) {
        const ctx = this.ctx;
        const markWidth = faceRadius * 1.05;
        const scale = markWidth / SHUL_STATION_MARK_VIEWBOX_WIDTH;

        ctx.save();
        ctx.beginPath();
        ctx.arc(this.center, this.center, faceRadius, 0, Math.PI * 2);
        ctx.clip();
        ctx.translate(this.center, this.center);
        ctx.scale(scale, scale);
        ctx.translate(-SHUL_STATION_MARK_VIEWBOX_WIDTH / 2, -SHUL_STATION_MARK_VIEWBOX_HEIGHT / 2);

        const engraveOffset = SHUL_STATION_MARK_VIEWBOX_WIDTH * 0.01;

        ctx.translate(engraveOffset, engraveOffset);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
        ctx.fill(SHUL_STATION_MARK_PATH, 'evenodd');
        ctx.translate(-engraveOffset, -engraveOffset);

        ctx.translate(-engraveOffset, -engraveOffset);
        ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
        ctx.fill(SHUL_STATION_MARK_PATH, 'evenodd');
        ctx.translate(engraveOffset, engraveOffset);

        ctx.fillStyle = this.palette.clockBody;
        ctx.globalAlpha = 0.7;
        ctx.fill(SHUL_STATION_MARK_PATH, 'evenodd');
        ctx.restore();
    }

    draw() {
        if (this.size <= 0 || this.radius <= 4 + this.radius * 0.05) {
            return;
        }

        const faceRadius = this.faceRadius;
        this.ctx.save();
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.ctx.drawImage(this.staticCanvas, 0, 0);
        this.ctx.restore();

        const now = new Date();
        const seconds = now.getSeconds() + now.getMilliseconds() / 1000;
        const minutes = now.getMinutes() + seconds / 60;
        const hours = (now.getHours() % 12) + minutes / 60;

        const secondAngle = (seconds * Math.PI / 30) - (Math.PI / 2);
        const minuteAngle = (minutes * Math.PI / 30) - (Math.PI / 2);
        const hourAngle = (hours * Math.PI / 6) - (Math.PI / 2);

        this.drawHand(hourAngle, faceRadius * 0.52, faceRadius * 0.07, faceRadius * 0.1, faceRadius * 0.04, this.palette.handHour);
        this.drawHand(minuteAngle, faceRadius * 0.8, faceRadius * 0.09, faceRadius * 0.075, faceRadius * 0.026, this.palette.handMinute);
        this.drawHand(secondAngle, faceRadius * 0.9, faceRadius * 0.24, faceRadius * 0.026, faceRadius * 0.018, this.palette.handSecond);

        this.drawCenterPin(faceRadius);
    }

    start() {
        const loop = () => {
            this.draw();
            this.animationFrameId = requestAnimationFrame(loop);
        };
        loop();
    }

    destroy() {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
        }
        this.resizeObserver.disconnect();
    }
}

export function initAnalogClock(canvas) {
    return new AnalogClock(canvas);
}
